import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useStatus } from '../../contexts/StatusContext';
import { downloadJSON, downloadPDF } from '../../utils/report';
import { classifyStatus } from '../../utils/checker';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiCopy, FiExternalLink, FiServer, FiShield, FiArrowRight, FiInfo, FiGlobe, FiActivity, FiLock } = FiIcons;

const ReportTab = () => {
  const { currentResult, settings } = useStatus();

  if (!currentResult) {
    return (
      <div className="text-center py-8 sm:py-12 text-gray-500 dark:text-gray-400">
        <SafeIcon icon={FiExternalLink} className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-4 opacity-50" />
        <p className="text-base sm:text-lg font-medium mb-2">No report available</p>
        <p className="text-xs sm:text-sm">Check a website to generate a detailed report</p>
      </div>
    );
  }

  const status = classifyStatus(currentResult, settings.thresholdMs);
  const isLimited = currentResult.mode === 'Limited';
  const isServerMode = currentResult.mode === 'Server';

  const handleDownloadJSON = () => {
    downloadJSON(currentResult);
  };

  const handleDownloadPDF = () => {
    downloadPDF(currentResult);
  };

  const handleCopySummary = () => {
    const summary = `Website: ${currentResult.input}
Mode: ${currentResult.mode}
Status: ${isLimited && status !== 'Down' ? `${status} (Limited)` : status}
Response Time: ${currentResult.responseTimeMs}ms
HTTP Status: ${currentResult.httpCode || 'N/A'}
Final URL: ${currentResult.finalUrl}
CORS Blocked: ${currentResult.corsBlocked ? 'Yes' : 'No'}
Checked: ${new Date(currentResult.timestamp).toLocaleString()}`;

    navigator.clipboard.writeText(summary);
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <motion.button
          onClick={handleDownloadJSON}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors focus-ring text-sm"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <SafeIcon icon={FiDownload} className="h-4 w-4" />
          <span>Download JSON</span>
        </motion.button>

        <motion.button
          onClick={handleDownloadPDF}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors focus-ring text-sm"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <SafeIcon icon={FiDownload} className="h-4 w-4" />
          <span>Download PDF</span>
        </motion.button>

        <motion.button
          onClick={handleCopySummary}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors focus-ring text-sm"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <SafeIcon icon={FiCopy} className="h-4 w-4" />
          <span>Copy Summary</span>
        </motion.button>
      </div>

      {/* Report Details */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
            <SafeIcon icon={FiExternalLink} className="h-5 w-5" />
            <span>Basic Information</span>
          </h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Input URL:</span>
              <p className="font-mono text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mt-1 break-all text-gray-900 dark:text-white">
                {currentResult.input}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Normalized URL:</span>
              <p className="font-mono text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mt-1 break-all text-gray-900 dark:text-white">
                {currentResult.normalizedUrl}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Final URL:</span>
              <p className="font-mono text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mt-1 break-all text-gray-900 dark:text-white">
                {currentResult.finalUrl}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Check Mode:</span>
              <p className="text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mt-1 text-gray-900 dark:text-white">
                {currentResult.mode}
                {currentResult.proxyUsed && ' (Proxy)'}
              </p>
            </div>
          </div>
        </div>

        {/* Response Headers - Enhanced for Server Mode */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
            <SafeIcon icon={FiServer} className="h-5 w-5" />
            <span>Response Headers</span>
            {isServerMode && (
              <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-2 py-1 rounded">
                Complete
              </span>
            )}
            {isLimited && (
              <SafeIcon icon={FiInfo} className="h-4 w-4 text-yellow-500" title="Headers unavailable due to CORS" />
            )}
          </h3>
          {currentResult.headers && Object.keys(currentResult.headers).length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {Object.entries(currentResult.headers)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, value]) => (
                <div key={key} className="text-sm">
                  <span className="font-medium text-gray-600 dark:text-gray-400">{key}:</span>
                  <p className="font-mono text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mt-1 break-all text-gray-900 dark:text-white">
                    {value}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isLimited ? 'Headers unavailable due to CORS' : 'No headers available'}
              </p>
            </div>
          )}
        </div>

        {/* Server Information - New Section for Server Mode */}
        {isServerMode && currentResult.serverInfo && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiActivity} className="h-5 w-5" />
              <span>Server Information</span>
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 p-3 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Server</span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {currentResult.serverInfo.server || 'Unknown'}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded">
                  <span className="text-sm text-gray-600 dark:text-gray-400">CDN</span>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {currentResult.serverInfo.cdn || 'None detected'}
                  </p>
                </div>
              </div>
              {currentResult.serverInfo.technology && currentResult.serverInfo.technology.length > 0 && (
                <div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Technologies:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {currentResult.serverInfo.technology.map((tech, index) => (
                      <span key={index} className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 px-2 py-1 rounded">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Security Analysis - Enhanced for Server Mode */}
        {isServerMode && currentResult.security && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiLock} className="h-5 w-5" />
              <span>Security Analysis</span>
              <span className={`text-xs px-2 py-1 rounded ${
                currentResult.security.securityScore >= 80 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                currentResult.security.securityScore >= 60 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' :
                'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
              }`}>
                Score: {currentResult.security.securityScore}/100
              </span>
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries({
                  'HTTPS': currentResult.security.https,
                  'CSP': currentResult.security.contentSecurityPolicy,
                  'X-Frame-Options': currentResult.security.xFrameOptions,
                  'X-Content-Type-Options': currentResult.security.xContentTypeOptions
                }).map(([key, value]) => (
                  <div key={key} className="bg-white dark:bg-gray-800 p-3 rounded flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{key}</span>
                    <span className={`text-sm font-medium ${
                      value ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {value === true ? '✓' : value === false ? '✗' : value || '✗'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Performance Metrics - New Section for Server Mode */}
        {isServerMode && currentResult.performance && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 xl:col-span-2">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiActivity} className="h-5 w-5" />
              <span>Performance Metrics</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">Redirects</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.performance.redirectCount}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">Protocol</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.performance.protocol?.replace(':', '').toUpperCase()}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">Compressed</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.performance.compressed ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">Content Size</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.performance.contentLength 
                    ? `${(currentResult.performance.contentLength / 1024).toFixed(1)} KB`
                    : 'Unknown'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* DNS Information - Enhanced */}
        {currentResult.dns && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiGlobe} className="h-5 w-5" />
              <span>DNS Information</span>
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400">A Records:</span>
                <div className="mt-1">
                  {currentResult.dns.aRecords && currentResult.dns.aRecords.length > 0 ? (
                    currentResult.dns.aRecords.map((ip, index) => (
                      <p key={index} className="font-mono text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mb-1 text-gray-900 dark:text-white">
                        {ip}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No A records found</p>
                  )}
                </div>
              </div>
              {currentResult.dns.ttl && (
                <div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">TTL:</span>
                  <p className="text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mt-1 text-gray-900 dark:text-white">
                    {currentResult.dns.ttl} seconds
                  </p>
                </div>
              )}
              {isServerMode && currentResult.dns.recordCount && (
                <div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Records:</span>
                  <p className="text-xs sm:text-sm bg-white dark:bg-gray-800 p-2 rounded mt-1 text-gray-900 dark:text-white">
                    {currentResult.dns.recordCount}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Limited Check Results */}
        {isLimited && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiInfo} className="h-5 w-5" />
              <span>Limited Check Results</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">DNS Resolution</span>
                <span className={`text-sm font-medium ${
                  currentResult.dnsResolved ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {currentResult.dnsResolved ? 'Success' : 'Failed'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">Favicon Test</span>
                <span className={`text-sm font-medium ${
                  currentResult.faviconReachable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {currentResult.faviconReachable ? 'Success' : 'Failed'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Redirect Chain */}
        {currentResult.redirects && currentResult.redirects.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 xl:col-span-2">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiArrowRight} className="h-5 w-5" />
              <span>Redirect Chain ({currentResult.redirects.length})</span>
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {currentResult.redirects.map((redirect, index) => (
                <div key={index} className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 p-3 bg-white dark:bg-gray-800 rounded">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs sm:text-sm text-gray-900 dark:text-white break-all">
                      {redirect.from}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      HTTP {redirect.code} {redirect.statusText && `- ${redirect.statusText}`}
                    </p>
                  </div>
                  <SafeIcon icon={FiArrowRight} className="h-4 w-4 text-gray-400 hidden sm:block" />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs sm:text-sm text-gray-900 dark:text-white break-all">
                      {redirect.to}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Information - Basic for non-server modes */}
        {!isServerMode && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 xl:col-span-2">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="h-5 w-5" />
              <span>Security Information</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">HTTPS</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.finalUrl?.startsWith('https://') || currentResult.ssl ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">TLS Certificate</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.finalUrl?.startsWith('https://') || currentResult.ssl ? 'Browser Verified' : 'N/A'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">CORS Status</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.corsBlocked ? 'Blocked' : 'Allowed'}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded">
                <span className="text-sm text-gray-600 dark:text-gray-400">Check Mode</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentResult.mode}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportTab;